import * as Yup from 'yup';
import { GpsType } from '../../../../../../../global/components/layout/layout-models';
import { getRequiredText } from '../../../../../../../global/helpers/transformer';
import { ContributionType, LayDcPlanAdoptionContentModel, ParticipantModel, QccoStatus } from '../../models/plan-adoption-form-models';
import { calculateQccoStatus, createQccoValidationSchema } from '../../shared/qcco-questions-shared';
import { PlanAdoptionRoutes } from '../../routes';
import { LayDcPlanAdoptionWizard } from './lay-dc-plan-adoption-wizard';

export class LayDcAdoptionDetailsWizard extends LayDcPlanAdoptionWizard {

    getFormContentModel(): LayDcPlanAdoptionContentModel {

        const institutionSettings = this.epiContent?.institutionSettingsState?.epiContent;
        const content = super.getFormContentModel();

        return {
            ...content,
            gpsType: GpsType.LayDcAdoptionDetail,
            pageTitle: institutionSettings?.adoptLayDCDetailsTitle,
            PageDescription: institutionSettings?.adoptLayDCDetailsDescription,
            submitLabel: institutionSettings?.adoptLayRSVPSubmitLabel,
            cancelLabel: institutionSettings?.adoptLayRSVPCancelLabel,
            isEmployerContributionRequired: true,
            docuSignSubject: institutionSettings?.adoptLayDCDocuSignSubject,
            docuSignFinishText: institutionSettings?.adoptLayDCDocusignFinishText,
            docuSignTemplateId: institutionSettings?.adoptLayDCDocuSignTemplateId,
        };
    }

    getPreviousStepUrl() {
        return `${this.wizardOptions?.featureRootPath}${PlanAdoptionRoutes.QccoQuestions}`;
    }

    getNextStepUrl() {
        return `${this.wizardOptions?.featureRootPath}${PlanAdoptionRoutes.Review}`;
    }

    // eslint-disable-next-line class-methods-use-this
    protected getAdditionalFieldValidationSchema(schema): any {
        return schema;
    }

    getFormValidationSchema(): any {

        const content = this.getFormContentModel();
        const labelSettings = this.epiContent?.labelSettingState?.epiContent;

        return Yup.lazy(({
            qccoQuestion1,
            qccoQuestion2,
            qccoQuestion3,
            allow_multiple_contributions,
        }) => {

            let schema = Yup.object().shape({});

            const qccoStatus = calculateQccoStatus(qccoQuestion1, qccoQuestion2, qccoQuestion3);

            schema = schema.shape({
                adoption_date: Yup.string().nullable().required(content?.adoptionDateLabel),
                minimum_scheduleds_hours: Yup.number().nullable()
                    .required(getRequiredText(labelSettings?.requiredFormat, content?.adoptMinScheduleHoursLabel))
                    .min(0, getRequiredText(labelSettings?.requiredFormat, content?.adoptMinScheduleHoursLabel)),

                employer_base_contribution_percentage: Yup.number().nullable()
                    .when([], {
                        is: () => {
                            return content?.isEmployerContributionRequired;
                        },
                        then: Yup.number().nullable().required(getRequiredText(labelSettings.requiredFormat, content?.adoptEmployerBaseContributionLabel)),
                    })
                    .min(content?.isEmployerContributionRequired ? 1 : 0, getRequiredText(content?.percentageFormatMessage, content?.adoptEmployerBaseContributionLabel))
                    .max(100, getRequiredText(content?.percentageFormatMessage, content?.adoptEmployerBaseContributionLabel)),

                employer_match_contribution_percentage: Yup.number().nullable()
                    .when([], {
                        is: () => {
                            return content?.isEmployerContributionRequired;
                        },
                        then: Yup.number().nullable().required(getRequiredText(labelSettings?.requiredFormat, content?.adoptEmployerMatchContributionLabel)),
                    })
                    .min(0, getRequiredText(content?.percentageFormatMessage, content?.adoptEmployerMatchContributionLabel))
                    .max(100, getRequiredText(content?.percentageFormatMessage, content?.adoptEmployerMatchContributionLabel)),

                ...createQccoValidationSchema(),
            });

            if (qccoStatus === QccoStatus.Qualified && allow_multiple_contributions) {
                const participant = Yup.object().shape({
                    contribution_percentage: Yup.number().nullable()
                        .min(0, getRequiredText(content?.percentageFormatMessage, content?.contributionAmountLabel))
                        .max(100, getRequiredText(content?.percentageFormatMessage, content?.adoptEmployerMatchContributionLabel)),
                    contribution_type: Yup.string().nullable()
                        .test('valid_contribution_type',
                            (value, context) => {
                                const participantValidate: ParticipantModel = context.parent;

                                if ((value === ContributionType.None) ||
                                    (!value && (participantValidate?.contribution_amount || participantValidate?.contribution_percentage))) {
                                    return context.createError({ message: getRequiredText(labelSettings?.requiredFormat, content?.contributionTypeLabel) });
                                }

                                return true;
                            }),
                    contribution_amount: Yup.number().nullable()
                        .min(0, getRequiredText(content?.amountFormatMessage, content?.contributionAmountLabel)),
                });

                schema = schema.shape({
                    participants: Yup.array().of(participant),
                });
            }

            schema = this.getAdditionalFieldValidationSchema(schema);

            return schema;
        });
    }
}